<template>
  <div class="tianyi-auto-upload-config">
    <el-card>
      <div slot="header" class="clearfix">
        <span>天翼云自动上传配置</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="showAddDialog">
          + 添加配置
        </el-button>
      </div>

      <el-table :data="configs" v-loading="loading" border>
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column label="账号" width="150">
          <template slot-scope="scope">
            {{ getAccountName(scope.row.account_id) }}
          </template>
        </el-table-column>
        <el-table-column prop="folder_name" label="保存文件夹" min-width="200" />
        <el-table-column label="状态" width="120">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.is_default" type="success">默认</el-tag>
            <el-tag v-else-if="scope.row.enabled" type="info">启用</el-tag>
            <el-tag v-else type="danger">禁用</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="180">
          <template slot-scope="scope">
            {{ formatTime(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220">
          <template slot-scope="scope">
            <el-button 
              v-if="!scope.row.is_default" 
              size="mini" 
              type="success" 
              @click="setDefault(scope.row)"
            >
              设为默认
            </el-button>
            <el-button 
              size="mini" 
              type="primary" 
              @click="editConfig(scope.row)"
            >
              编辑
            </el-button>
            <el-button 
              size="mini" 
              type="danger" 
              @click="deleteConfig(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 添加/编辑对话框 -->
    <el-dialog 
      :title="editingId ? '编辑配置' : '添加配置'" 
      :visible.sync="dialogVisible" 
      width="800px"
      top="5vh"
    >
      <el-form :model="form" label-width="100px">
        <!-- 选择账号 -->
        <el-form-item label="选择账号" required>
          <el-select 
            v-model="form.account_id" 
            placeholder="请选择天翼云账号"
            @change="handleAccountChange"
            style="width: 100%"
          >
            <el-option
              v-for="account in tianyiAccounts"
              :key="account.id"
              :label="account.name"
              :value="account.id"
            />
          </el-select>
        </el-form-item>
        
        <!-- 选择保存文件夹 -->
        <el-form-item label="保存位置">
          <div class="folder-browser">
            <div class="folder-toolbar">
              <el-breadcrumb separator="/">
                <el-breadcrumb-item 
                  @click.native="goToRoot" 
                  style="cursor: pointer"
                >
                  <i class="el-icon-folder-opened"></i> 根目录
                </el-breadcrumb-item>
                <el-breadcrumb-item 
                  v-for="(folder, index) in folderPath" 
                  :key="folder.id"
                  @click.native="goToFolder(index)"
                  style="cursor: pointer"
                >
                  {{ folder.name }}
                </el-breadcrumb-item>
              </el-breadcrumb>
              <el-button 
                size="small" 
                icon="el-icon-back"
                @click="goToParent" 
                :disabled="currentFolderId === '-11'"
              >
                返回上级
              </el-button>
            </div>
            
            <el-table 
              :data="folderList" 
              v-loading="foldersLoading" 
              border 
              size="small"
              height="300"
              @row-dblclick="enterFolder"
            >
              <el-table-column prop="name" label="文件夹名称">
                <template slot-scope="scope">
                  <template v-if="scope.row.isFolder">
                    <i class="el-icon-folder" style="color: #f0c040; margin-right: 5px"></i>
                    {{ scope.row.name }}
                  </template>
                  <template v-else>
                    <i class="el-icon-document" style="color: #909399; margin-right: 5px"></i>
                    <span style="color: #909399">{{ scope.row.name }}</span>
                  </template>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="100">
                <template slot-scope="scope">
                  <el-button 
                    v-if="scope.row.isFolder"
                    size="mini" 
                    @click="enterFolder(scope.row)"
                  >
                    进入
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            
            <div class="folder-selected">
              <el-tag type="success" size="medium">
                <i class="el-icon-folder-checked"></i>
                当前选择: {{ form.folder_name || '根目录' }}
              </el-tag>
            </div>
          </div>
        </el-form-item>
        
        <el-form-item label="设为默认">
          <el-switch v-model="form.is_default" />
          <span class="form-tip">默认配置将用于自动上传任务</span>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveConfig" :loading="saving">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getAllAutoUploadConfigs,
  saveAutoUploadConfig,
  deleteAutoUploadConfig,
  setDefaultAutoUploadConfig,
  getTianyiAccounts,
  listTianyiFiles
} from '@/api'

export default {
  name: 'TianyiAutoUploadConfig',
  data() {
    return {
      configs: [],
      loading: false,
      dialogVisible: false,
      saving: false,
      editingId: null,
      form: {
        account_id: null,
        folder_id: '-11',
        folder_name: '根目录',
        is_default: false
      },
      // 天翼云账号列表
      tianyiAccounts: [],
      // 文件夹浏览
      folderList: [],
      foldersLoading: false,
      currentFolderId: '-11',
      folderPath: [],
      folderHistory: []
    }
  },
  mounted() {
    this.loadConfigs()
    this.loadTianyiAccounts()
  },
  methods: {
    // 获取账号名称
    getAccountName(accountId) {
      const account = this.tianyiAccounts.find(a => a.id === accountId)
      return account ? account.name : `账号 ${accountId}`
    },

    // 加载配置列表
    async loadConfigs() {
      this.loading = true
      try {
        const res = await getAllAutoUploadConfigs()
        if (res.success) {
          this.configs = res.data
        } else {
          this.$message.error(res.message || '加载失败')
        }
      } catch (e) {
        this.$message.error('加载失败')
      }
      this.loading = false
    },

    // 加载天翼云账号
    async loadTianyiAccounts() {
      try {
        const res = await getTianyiAccounts()
        if (res.success) {
          this.tianyiAccounts = res.data
        }
      } catch (e) {
        console.error('加载天翼云账号失败', e)
      }
    },

    // 显示添加对话框
    showAddDialog() {
      this.editingId = null
      this.form = {
        account_id: this.tianyiAccounts.length > 0 ? this.tianyiAccounts[0].id : null,
        folder_id: '-11',
        folder_name: '根目录',
        is_default: false
      }
      this.currentFolderId = '-11'
      this.folderPath = []
      this.folderHistory = []
      this.folderList = []
      this.dialogVisible = true
      
      // 自动加载文件夹
      if (this.form.account_id) {
        this.loadFolders()
      }
    },

    // 编辑配置
    editConfig(config) {
      this.editingId = config.id
      this.form = {
        account_id: config.account_id,
        folder_id: config.folder_id,
        folder_name: config.folder_name,
        is_default: config.is_default === 1
      }
      this.currentFolderId = config.folder_id || '-11'
      this.folderPath = []
      this.folderHistory = []
      this.dialogVisible = true
      this.loadFolders()
    },

    // 账号切换
    handleAccountChange() {
      this.currentFolderId = '-11'
      this.folderPath = []
      this.folderHistory = []
      this.form.folder_id = '-11'
      this.form.folder_name = '根目录'
      this.loadFolders()
    },

    // 加载文件夹列表
    async loadFolders() {
      if (!this.form.account_id) return
      this.foldersLoading = true
      try {
        const res = await listTianyiFiles(this.form.account_id, { folderId: this.currentFolderId })
        if (res.success) {
          // 只显示文件夹，文件显示为灰色
          this.folderList = res.data
        } else {
          this.$message.error(res.message || '加载文件夹失败')
        }
      } catch (e) {
        this.$message.error('加载文件夹失败')
      }
      this.foldersLoading = false
    },

    // 进入文件夹
    enterFolder(folder) {
      if (!folder.isFolder) return
      
      this.folderHistory.push(this.currentFolderId)
      this.folderPath.push({ id: folder.id, name: folder.name })
      this.currentFolderId = folder.id
      this.form.folder_id = folder.id
      this.form.folder_name = this.folderPath.map(f => f.name).join('/')
      this.loadFolders()
    },

    // 返回上级
    goToParent() {
      if (this.folderHistory.length > 0) {
        this.currentFolderId = this.folderHistory.pop()
        this.folderPath.pop()
        this.form.folder_id = this.currentFolderId
        this.form.folder_name = this.folderPath.length > 0 
          ? this.folderPath.map(f => f.name).join('/') 
          : '根目录'
        this.loadFolders()
      }
    },

    // 返回根目录
    goToRoot() {
      this.currentFolderId = '-11'
      this.folderPath = []
      this.folderHistory = []
      this.form.folder_id = '-11'
      this.form.folder_name = '根目录'
      this.loadFolders()
    },

    // 跳转到指定层级
    goToFolder(index) {
      // 截取到指定层级
      this.folderPath = this.folderPath.slice(0, index + 1)
      this.folderHistory = this.folderHistory.slice(0, index + 1)
      this.currentFolderId = this.folderPath[index].id
      this.form.folder_id = this.currentFolderId
      this.form.folder_name = this.folderPath.map(f => f.name).join('/')
      this.loadFolders()
    },

    // 保存配置
    async saveConfig() {
      if (!this.form.account_id) {
        this.$message.warning('请选择天翼云账号')
        return
      }

      this.saving = true
      try {
        const res = await saveAutoUploadConfig(this.form)
        if (res.success) {
          this.$message.success('保存成功')
          this.dialogVisible = false
          this.loadConfigs()
        } else {
          this.$message.error(res.message || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存失败')
      }
      this.saving = false
    },

    // 删除配置
    async deleteConfig(config) {
      try {
        await this.$confirm('确定要删除此配置吗？', '提示', {
          type: 'warning'
        })
        const res = await deleteAutoUploadConfig(config.id)
        if (res.success) {
          this.$message.success('删除成功')
          this.loadConfigs()
        } else {
          this.$message.error(res.message || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },

    // 设为默认
    async setDefault(config) {
      try {
        const res = await setDefaultAutoUploadConfig(config.id)
        if (res.success) {
          this.$message.success('已设置为默认配置')
          this.loadConfigs()
        } else {
          this.$message.error(res.message || '设置失败')
        }
      } catch (e) {
        this.$message.error('设置失败')
      }
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return '-'
      const date = new Date(time)
      return date.toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.tianyi-auto-upload-config {
  padding: 20px;
}

.folder-browser {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 10px;
}

.folder-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.folder-selected {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #ebeef5;
}

.form-tip {
  color: #909399;
  font-size: 12px;
  margin-left: 10px;
}
</style>
